import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FileTextIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function RequisitionSummaryReport({
  filters,
  locale
}) {
  // Sample data for requisition summary
  const statusData = [{
    name: 'Draft',
    value: 24
  }, {
    name: 'Pending Approval',
    value: 18
  }, {
    name: 'Approved',
    value: 42
  }, {
    name: 'Rejected',
    value: 8
  }, {
    name: 'Converted to PO',
    value: 38
  }];
  const COLORS = ['#94a3b8', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];
  const departmentData = [{
    department: 'IT',
    count: 35,
    value: 125000
  }, {
    department: 'Marketing',
    count: 28,
    value: 87500
  }, {
    department: 'Operations',
    count: 25,
    value: 110000
  }, {
    department: 'Sales',
    count: 18,
    value: 62500
  }, {
    department: 'HR',
    count: 12,
    value: 45000
  }, {
    department: 'Finance',
    count: 10,
    value: 38000
  }];
  const requisitionData = [{
    id: 'REQ-2023-001',
    name: 'Office Supplies',
    requestor: 'John Smith',
    department: 'Operations',
    status: 'Approved',
    date: '2023-10-01',
    value: 2500,
    aging: 12
  }, {
    id: 'REQ-2023-002',
    name: 'Software Licenses',
    requestor: 'Sarah Johnson',
    department: 'IT',
    status: 'Pending Approval',
    date: '2023-10-05',
    value: 8500,
    aging: 8
  }, {
    id: 'REQ-2023-003',
    name: 'Marketing Materials',
    requestor: 'David Lee',
    department: 'Marketing',
    status: 'Converted to PO',
    date: '2023-09-28',
    value: 4200,
    aging: 15
  }, {
    id: 'REQ-2023-004',
    name: 'Office Furniture',
    requestor: 'Emily Chen',
    department: 'HR',
    status: 'Draft',
    date: '2023-10-10',
    value: 12000,
    aging: 3
  }, {
    id: 'REQ-2023-005',
    name: 'Training Services',
    requestor: 'Michael Brown',
    department: 'Sales',
    status: 'Rejected',
    date: '2023-10-02',
    value: 5500,
    aging: 11
  }];
  const totalRequisitions = statusData.reduce((sum, item) => sum + item.value, 0);
  const totalValue = departmentData.reduce((sum, item) => sum + item.value, 0);
  const getStatusColor = status => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-400';
      case 'Pending Approval':
        return 'bg-yellow-500';
      case 'Approved':
        return 'bg-green-500';
      case 'Rejected':
        return 'bg-red-500';
      case 'Converted to PO':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="p-6" data-id="element-16371">
      <div className="mb-8" data-id="element-16372">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-16373">
          Requisition Summary Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16374">
          Status and aging analysis by entity, user, or department
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-16375">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16376">
          <div className="p-5" data-id="element-16377">
            <div className="flex items-center" data-id="element-16378">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-16379">
                <FileTextIcon className="h-6 w-6 text-white" data-id="element-16380" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16381">
                <dl data-id="element-16382">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16383">
                    Total Requisitions
                  </dt>
                  <dd data-id="element-16384">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16385">
                      {totalRequisitions}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16386">
          <div className="p-5" data-id="element-16387">
            <div className="flex items-center" data-id="element-16388">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-16389">
                <CheckCircleIcon className="h-6 w-6 text-white" data-id="element-16390" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16391">
                <dl data-id="element-16392">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16393">
                    Approval Rate
                  </dt>
                  <dd data-id="element-16394">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16395">
                      {Math.round(statusData.find(s => s.name === 'Approved').value / totalRequisitions * 100)}
                      %
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16396">
          <div className="p-5" data-id="element-16397">
            <div className="flex items-center" data-id="element-16398">
              <div className="flex-shrink-0 bg-purple-500 rounded-md p-3" data-id="element-16399">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-16400" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16401">
                <dl data-id="element-16402">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16403">
                    Avg. Approval Time
                  </dt>
                  <dd data-id="element-16404">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16405">
                      2.3 days
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-16406">
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-16407">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16408">
            Requisition Status
          </h4>
          <div className="h-80" data-id="element-16409">
            <ResponsiveContainer width="100%" height="100%" data-id="element-16410">
              <PieChart data-id="element-16411">
                <Pie data={statusData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                percent,
                index
              }) => {
                const RADIAN = Math.PI / 180;
                const radius = 25 + innerRadius + (outerRadius - innerRadius);
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                return <text x={x} y={y} fill="#000" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" data-id="element-16413">
                        {statusData[index].name} ({(percent * 100).toFixed(0)}%)
                      </text>;
              }} data-id="element-16412">
                  {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-16414" />)}
                </Pie>
                <Tooltip data-id="element-16415" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-16416">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16417">
            Requisitions by Department
          </h4>
          <div className="h-80" data-id="element-16418">
            <ResponsiveContainer width="100%" height="100%" data-id="element-16419">
              <BarChart data={departmentData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-16420">
                <CartesianGrid strokeDasharray="3 3" data-id="element-16421" />
                <XAxis dataKey="department" data-id="element-16422" />
                <YAxis yAxisId="left" orientation="left" stroke="#8884d8" data-id="element-16423" />
                <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" data-id="element-16424" />
                <Tooltip data-id="element-16425" />
                <Legend data-id="element-16426" />
                <Bar yAxisId="left" dataKey="count" name="Count" fill="#8884d8" data-id="element-16427" />
                <Bar yAxisId="right" dataKey="value" name="Value" fill="#82ca9d" data-id="element-16428" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Requisition Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-16429">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-16430">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16431">
            Recent Requisitions
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16432">
            Details of recent requisition activity
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-16433">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-16434">
            <thead className="bg-gray-50" data-id="element-16435">
              <tr data-id="element-16436">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16437">
                  Requisition
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16438">
                  Requestor / Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16439">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16440">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16441">
                  Value
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16442">
                  Aging (days)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-16443">
              {requisitionData.map(req => <tr key={req.id} data-id="element-16444">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-16445">
                    {req.name}
                    <div className="text-xs text-gray-500" data-id="element-16446">{req.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16447">
                    {req.requestor}
                    <div className="text-xs text-gray-400" data-id="element-16448">
                      {req.department}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-16449">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(req.status)} text-white`} data-id="element-16450">
                      {req.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16451">
                    {req.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-16452">
                    {formatCurrency(req.value, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-16453">
                    {req.aging}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}